import { Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthPageLayoutComponent } from '../../../layout/auth-page-layout/auth-page-layout.component';
import { AuthService } from '../../../shared/services/auth.service';
import { AuthStateService } from '../../../shared/states/auth.state';

/**
 * Landing page for the admin panel's "Quick Login" action.
 *
 * The admin panel sends the browser here with a single-use, 60-second token
 * (?token=…). We swap it for a normal session, remember where the admin came
 * from, and drop the user on the main feed. Signing out later returns the
 * browser to the admin panel (see AuthStateService.getLogoutRedirectUrl).
 */
@Component({
  selector: 'app-quick-login',
  standalone: true,
  imports: [CommonModule, RouterModule, AuthPageLayoutComponent],
  templateUrl: './quick-login.html',
})
export class QuickLogin implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private authState = inject(AuthStateService);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  status = signal<'loading' | 'error'>('loading');
  message = signal('');

  ngOnInit(): void {
    if (!this.isBrowser) return;

    const token = this.route.snapshot.queryParamMap.get('token');

    // Get the token out of the address bar / history straight away.
    this.router.navigate([], { queryParams: {}, replaceUrl: true });

    if (!token) {
      this.fail('This quick login link is invalid.');
      return;
    }

    // Whoever was signed in on this browser before is replaced by the
    // impersonated user; their stored session is simply overwritten.
    const clientId = this.authState.getClientId();

    this.authService.quickLogin(token, clientId ?? undefined).subscribe({
      next: (res: any) => {
        const data = res?.data ?? {};
        this.authState.setAuth({
          isLoggedIn: true,
          user: data.user ?? null,
          token: data.accessToken,
          refreshToken: data.refreshToken,
          quickLogin: {
            returnUrl: data.quickLogin?.returnUrl ?? null,
            issuedBy: data.quickLogin?.issuedBy ?? null,
          },
        });
        // App root connects sockets on auth change, so nothing else to do.
        this.router.navigate(['/all'], { replaceUrl: true });
      },
      error: (err: any) => {
        this.fail(err?.error?.message || 'This quick login link is invalid or has expired.');
      },
    });
  }

  private fail(message: string): void {
    this.message.set(message);
    this.status.set('error');
  }
}
